/**
 * Composant d'affichage des souscriptions pour l'espace membre
 * Design ultra-moderne avec filtres avancés et statistiques
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, RefreshCw, FilePlus, Search, X, Filter, ChevronDown, ChevronUp, TrendingUp, Clock, CheckCircle, Euro, ArrowUpDown, Shield, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { SouscriptionBrouillonCard, STATUS_CONFIG, getMontantTTC, getDocType } from './SouscriptionBrouillonCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SouscriptionsAvecBrouillons({ souscriptions = [], onRefresh, getAuthHeader }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // 🔒 Sécurité défense-en-profondeur :
  // L'API /api/souscriptions/me filtre déjà par membre_id/member_id, mais on
  // applique ici un second filtre côté client pour garantir que la liste
  // affichée ne contient strictement QUE les dispositifs dont l'utilisateur
  // connecté est titulaire — quel que soit l'angle (membre_id, member_id,
  // user_id, ou correspondance email/SIRET via le profil membre).
  const ownedSouscriptions = useMemo(() => {
    if (!user?.id) return [];
    const allowedIds = new Set([user.id, user.membre_id, user.member_id].filter(Boolean));
    const userEmail = (user.email || '').toLowerCase().trim();
    return souscriptions.filter((sub) => {
      const mid = sub.membre_id || sub.member_id || sub.user_id;
      if (mid && allowedIds.has(mid)) return true;
      // Fallback email pour les très anciennes entrées non rattachées par id
      const subEmail = (sub.email || sub.user_email || '').toLowerCase().trim();
      if (userEmail && subEmail && subEmail === userEmail) return true;
      return false;
    });
  }, [souscriptions, user]);

  // États des filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterMontantMin, setFilterMontantMin] = useState('');
  const [filterMontantMax, setFilterMontantMax] = useState('');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');

  // Calcul des statistiques
  const stats = useMemo(() => {
    const total = ownedSouscriptions.length;
    const contrats = ownedSouscriptions.filter(s => s.type_document !== 'avenant' && !s.numero_police?.startsWith('AV-')).length;
    const avenants = ownedSouscriptions.filter(s => s.type_document === 'avenant' || s.numero_police?.startsWith('AV-')).length;
    const valides = ownedSouscriptions.filter(s => s.statut === 'validee' || s.statut === 'VALIDE' || s.paiement_valide).length;
    const enAttente = ownedSouscriptions.filter(s => 
      s.statut === 'en_attente' || s.statut === 'en_attente_paiement' || s.statut === 'en_attente_signature'
    ).length;
    const brouillons = ownedSouscriptions.filter(s => s.statut === 'brouillon').length;
    
    const montantTotal = ownedSouscriptions.reduce((sum, s) => {
      const montant = s.montant_ttc || s.tarification?.montant_ttc || 
        (s.montant_ht_contribution || s.tarification?.montant_ht || 0) * 1.275;
      return sum + (s.paiement_valide ? montant : 0);
    }, 0);

    return { total, contrats, avenants, valides, enAttente, brouillons, montantTotal };
  }, [ownedSouscriptions]);

  // Fonction de calcul du montant TTC
  // getMontantTTC / getDocType importés depuis SouscriptionBrouillonCard

  // Filtrage et tri (toujours sur la liste filtrée par propriétaire)
  const filteredAndSortedSouscriptions = useMemo(() => {
    let result = [...ownedSouscriptions];

    // Recherche textuelle
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(sub => 
        (sub.raison_sociale || '').toLowerCase().includes(searchLower) ||
        (sub.numero_police || '').toLowerCase().includes(searchLower) ||
        (sub.siret || '').toLowerCase().includes(searchLower) ||
        (sub.id || '').toLowerCase().includes(searchLower)
      );
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      if (filterStatus === 'actif') {
        result = result.filter(sub => 
          (sub.statut === 'validee' || sub.statut === 'VALIDE' || sub.paiement_valide) && 
          getDocType(sub) !== 'avenant'
        );
      } else if (filterStatus === 'en_attente') {
        result = result.filter(sub => 
          sub.statut === 'en_attente' || sub.statut === 'en_attente_paiement' || sub.statut === 'en_attente_signature'
        );
      } else {
        result = result.filter(sub => sub.statut === filterStatus);
      }
    }

    // Filtre par type
    if (filterType !== 'all') {
      result = result.filter(sub => getDocType(sub) === filterType);
    }

    // Filtre par montant
    if (filterMontantMin) {
      result = result.filter(sub => getMontantTTC(sub) >= parseFloat(filterMontantMin));
    }
    if (filterMontantMax) {
      result = result.filter(sub => getMontantTTC(sub) <= parseFloat(filterMontantMax));
    }

    // Filtre par date
    if (filterDateDebut) {
      const dateDebut = new Date(filterDateDebut);
      result = result.filter(sub => new Date(sub.date_creation || sub.created_at) >= dateDebut);
    }
    if (filterDateFin) {
      const dateFin = new Date(filterDateFin);
      result = result.filter(sub => new Date(sub.date_creation || sub.created_at) <= dateFin);
    }

    // Tri
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return new Date(a.date_creation || a.created_at) - new Date(b.date_creation || b.created_at);
        case 'date_desc':
          return new Date(b.date_creation || b.created_at) - new Date(a.date_creation || a.created_at);
        case 'montant_asc':
          return getMontantTTC(a) - getMontantTTC(b);
        case 'montant_desc':
          return getMontantTTC(b) - getMontantTTC(a);
        case 'raison_sociale':
          return (a.raison_sociale || '').localeCompare(b.raison_sociale || '');
        default:
          return new Date(b.date_creation || b.created_at) - new Date(a.date_creation || a.created_at);
      }
    });

    return result;
  }, [ownedSouscriptions, searchTerm, filterStatus, filterType, filterMontantMin, filterMontantMax, filterDateDebut, filterDateFin, sortBy]);

  // Reset des filtres
  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setSortBy('date_desc');
    setFilterMontantMin('');
    setFilterMontantMax('');
    setFilterDateDebut('');
    setFilterDateFin('');
  };

  const hasActiveFilters = searchTerm || filterStatus !== 'all' || filterType !== 'all' || 
    filterMontantMin || filterMontantMax || filterDateDebut || filterDateFin;

  // Actions
  const handleReprendreSouscription = (souscriptionId) => {
    navigate(`/cooper/souscription-annuelle?resume=${souscriptionId}`);
    toast.info('Reprise de votre souscription en cours...');
  };

  // Rouvrir la souscription dans le calculateur BATICO avec les activités
  // cochées restaurées (modification puis nouvelle version de la proposition).
  const handleRouvrirSimulateur = (sub) => {
    navigate(`/calculateur-batico?from=souscription_resume&souscription=${sub.id}`);
    toast.info('Ouverture du simulateur avec vos activités…');
  };

  const handleDeleteBrouillon = (souscriptionId) => {
    // Masquage optimiste + fenêtre d'annulation de 5s avant suppression réelle
    setPendingDeleteIds((prev) => [...prev, souscriptionId]);
    const timer = setTimeout(async () => {
      try {
        await axios.delete(
          `${BACKEND_URL}/api/souscriptions-rcd/me/${souscriptionId}`,
          { headers: getAuthHeader() }
        );
        onRefresh();
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Erreur lors de la suppression');
        setPendingDeleteIds((prev) => prev.filter((id) => id !== souscriptionId));
      }
    }, 5000);
    toast('Brouillon supprimé', {
      duration: 5000,
      action: {
        label: 'Annuler',
        onClick: () => {
          clearTimeout(timer);
          setPendingDeleteIds((prev) => prev.filter((id) => id !== souscriptionId));
          toast.info('Suppression annulée');
        },
      },
    });
  };

  const handleDownloadRecuESS = async (sub) => {
    try {
      const recuId = sub.recu_ess_id;
      if (!recuId) {
        toast.error('Aucun reçu ESS disponible');
        return;
      }
      const response = await axios.get(
        `${BACKEND_URL}/api/recus-ess/${recuId}/download`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recu_ESS_${sub.recu_ess_numero || sub.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reçu ESS téléchargé !');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDownloadRecu = async (sub) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/souscriptions/${sub.id}/download-recu`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recu_${sub.numero_police || sub.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reçu téléchargé !');
    } catch {
      toast.error('Erreur lors du téléchargement du reçu');
    }
  };

  // V36.7 — Téléchargement du mini-PDF "Échéancier seul"
  const handleDownloadEcheancier = async (sub) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/souscriptions/${sub.id}/echeancier-pdf`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Echeancier_${sub.reference_solidaire || sub.numero_police || sub.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Échéancier téléchargé !');
    } catch (error) {
      const status = error?.response?.status;
      // Best-effort : lire le detail JSON même quand on attendait un blob
      let detail = 'Erreur lors du téléchargement de l\'échéancier';
      try {
        const blob = error?.response?.data;
        if (blob && typeof blob.text === 'function') {
          const txt = await blob.text();
          const j = JSON.parse(txt);
          if (j?.detail) detail = j.detail;
        }
      } catch { /* ignore */ }
      if (status === 400 && /non éligible/i.test(detail)) {
        toast.info(detail);
      } else {
        toast.error(detail);
      }
    }
  };


  return (
    <div className="space-y-6">
      {/* Header avec statistiques — Premium O'SCOP unifié */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { key: 'total', label: 'Total', value: stats.total, Icon: BarChart3, accent: 'navy', highlight: true },
          { key: 'contrats', label: 'Contrats', value: stats.contrats, Icon: FileText, accent: 'navy-soft' },
          { key: 'avenants', label: 'Avenants', value: stats.avenants, Icon: FilePlus, accent: 'gold' },
          { key: 'valides', label: 'Validés', value: stats.valides, Icon: CheckCircle, accent: 'emerald' },
          { key: 'attente', label: 'En attente', value: stats.enAttente, Icon: Clock, accent: 'gold-ring' },
          { key: 'montant', label: 'Montant validé', value: `${stats.montantTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`, Icon: Euro, accent: 'navy-gold' },
        ].map(({ key, label, value, Icon, accent, highlight }) => {
          const palettes = {
            navy: { bar: 'bg-[#0a3d62]', iconBg: 'bg-[#0a3d62]/8 group-hover:bg-[#0a3d62]/14', iconColor: 'text-[#0a3d62]', valueColor: 'text-[#0a3d62]', labelColor: 'text-[#0a3d62]/70' },
            'navy-soft': { bar: 'bg-[#0c4a78]', iconBg: 'bg-[#0a3d62]/6 group-hover:bg-[#0a3d62]/12', iconColor: 'text-[#0c4a78]', valueColor: 'text-[#0a3d62]', labelColor: 'text-[#0a3d62]/60' },
            gold: { bar: 'bg-[#DEA900]', iconBg: 'bg-[#DEA900]/12 group-hover:bg-[#DEA900]/20', iconColor: 'text-[#b88600]', valueColor: 'text-[#0a3d62]', labelColor: 'text-[#b88600]' },
            'gold-ring': { bar: 'bg-[#DEA900]', iconBg: 'bg-[#DEA900]/14 group-hover:bg-[#DEA900]/22', iconColor: 'text-[#b88600]', valueColor: 'text-[#b88600]', labelColor: 'text-[#b88600]' },
            'navy-gold': { bar: 'bg-gradient-to-b from-[#0a3d62] to-[#DEA900]', iconBg: 'bg-[#DEA900]/12 group-hover:bg-[#DEA900]/20', iconColor: 'text-[#b88600]', valueColor: 'text-[#0a3d62]', labelColor: 'text-[#0a3d62]/70' },
            emerald: { bar: 'bg-emerald-500', iconBg: 'bg-emerald-50 group-hover:bg-emerald-100', iconColor: 'text-emerald-600', valueColor: 'text-emerald-700', labelColor: 'text-emerald-700' },
          }[accent];
          const isMontant = key === 'montant';
          return (
            <div
              key={key}
              data-testid={`disp-stat-${key}`}
              className={`group relative bg-white border border-slate-200/80 rounded-2xl p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(10,61,98,0.18)] ${
                highlight ? 'ring-1 ring-[#0a3d62]/15 shadow-[0_4px_16px_-6px_rgba(10,61,98,0.15)]' : ''
              } ${accent === 'gold-ring' ? 'ring-1 ring-[#DEA900]/30' : ''}`}
            >
              <span className={`absolute left-0 top-4 bottom-4 w-1 rounded-r-full ${palettes.bar}`} />
              <div className="flex items-center justify-between pl-2">
                <div className="min-w-0">
                  <p className={`text-[10px] font-bold uppercase tracking-wider ${palettes.labelColor}`}>{label}</p>
                  <p className={`${isMontant ? 'text-lg' : 'text-2xl'} font-bold leading-none tabular-nums tracking-tight mt-1 ${palettes.valueColor}`}>{value}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${palettes.iconBg}`}>
                  <Icon className={`h-5 w-5 ${palettes.iconColor}`} strokeWidth={2.2} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Barre de progression globale — Premium */}
      {stats.total > 0 && (
        <div className="relative bg-white border border-slate-200/80 rounded-2xl p-4 ring-1 ring-emerald-200/60 shadow-[0_6px_20px_-10px_rgba(16,185,129,0.20)]" data-testid="disp-progress-premium">
          <span className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-emerald-500" />
          <div className="pl-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <span className="font-semibold text-[#0a3d62] text-sm">Taux de validation</span>
              </div>
              <span className="text-2xl font-bold tabular-nums text-emerald-700">
                {Math.round((stats.valides / stats.total) * 100)}%
              </span>
            </div>
            <div className="h-2.5 bg-emerald-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all duration-700"
                style={{ width: `${(stats.valides / stats.total) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-[11px]">
              <span className="text-emerald-700 font-semibold">{stats.valides} validé(s)</span>
              <span className="text-[#b88600] font-semibold">{stats.enAttente + stats.brouillons} en cours</span>
            </div>
          </div>
        </div>
      )}

      {/* Barre de recherche et filtres */}
      <Card className="border-2 border-gray-200">
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Ligne principale */}
            <div className="flex flex-wrap gap-3 items-center">
              {/* Recherche */}
              <div className="relative flex-1 min-w-[250px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  data-testid="search-mes-dispositifs-input"
                  placeholder="Rechercher dans mes dispositifs (raison sociale, référence, SIRET...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 border-2 focus:border-blue-500"
                  aria-label="Rechercher dans mes propres dispositifs uniquement"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="clear-search-btn"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Filtre Type */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40 h-11 border-2">
                  <FileText className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="contrat">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      Contrats
                    </span>
                  </SelectItem>
                  <SelectItem value="avenant">
                    <span className="flex items-center gap-2">
                      <FilePlus className="h-4 w-4 text-purple-500" />
                      Avenants
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Filtre Statut */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-44 h-11 border-2">
                  <Shield className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="actif">Actifs</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="brouillon">Brouillons</SelectItem>
                  <SelectItem value="validee">Validés</SelectItem>
                </SelectContent>
              </Select>

              {/* Tri */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-44 h-11 border-2">
                  <ArrowUpDown className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Plus récent</SelectItem>
                  <SelectItem value="date_asc">Plus ancien</SelectItem>
                  <SelectItem value="montant_desc">Montant ↓</SelectItem>
                  <SelectItem value="montant_asc">Montant ↑</SelectItem>
                  <SelectItem value="raison_sociale">Raison sociale</SelectItem>
                </SelectContent>
              </Select>

              {/* Toggle filtres avancés */}
              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="h-11 gap-2"
              >
                <Filter className="h-4 w-4" />
                Plus
                {showAdvancedFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>

              {/* Reset */}
              {hasActiveFilters && (
                <Button variant="ghost" onClick={resetFilters} className="h-11 text-gray-500">
                  <X className="h-4 w-4 mr-1" />
                  Réinitialiser
                </Button>
              )}

              {/* Rafraîchir */}
              <Button variant="outline" onClick={onRefresh} className="h-11">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            {/* Filtres avancés */}
            {showAdvancedFilters && (
              <div className="flex flex-wrap gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-500 whitespace-nowrap">Montant Global</Label>
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filterMontantMin}
                    onChange={(e) => setFilterMontantMin(e.target.value)}
                    className="w-28 h-9"
                  />
                  <span className="text-gray-400">-</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filterMontantMax}
                    onChange={(e) => setFilterMontantMax(e.target.value)}
                    className="w-28 h-9"
                  />
                  <span className="text-gray-500">€</span>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-500 whitespace-nowrap">Période</Label>
                  <DatePickerFr
                    value={filterDateDebut}
                    onChange={(v) => setFilterDateDebut(v)}
                    className="w-36 h-9"
                  />
                  <span className="text-gray-400">→</span>
                  <DatePickerFr
                    value={filterDateFin}
                    onChange={(v) => setFilterDateFin(v)}
                    className="w-36 h-9"
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Compteur de résultats — recherche cantonnée aux dispositifs propres au membre */}
      <div className="flex items-center justify-between text-sm" data-testid="dispositifs-counter">
        <span className="text-gray-600 inline-flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
          <span>
            <strong className="text-gray-800">{filteredAndSortedSouscriptions.length}</strong>
            {' '}sur{' '}
            <strong className="text-gray-800">{ownedSouscriptions.length}</strong>
            {' '}de vos dispositifs
            {hasActiveFilters && <span className="text-blue-600 ml-1">(filtré)</span>}
          </span>
        </span>
      </div>

      {/* Liste des souscriptions */}
      {filteredAndSortedSouscriptions.length === 0 ? (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune souscription trouvée</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {hasActiveFilters 
                ? 'Modifiez vos critères de recherche pour afficher plus de résultats.'
                : 'Vous n\'avez pas encore de souscription.'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={resetFilters} className="mt-4">
                Réinitialiser les filtres
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredAndSortedSouscriptions.filter((sub) => !pendingDeleteIds.includes(sub.id)).map((sub) => (
            <SouscriptionBrouillonCard
              key={sub.id}
              sub={sub}
              navigate={navigate}
              onReprendre={handleReprendreSouscription}
              onDelete={handleDeleteBrouillon}
              onDownloadRecuESS={handleDownloadRecuESS}
              onDownloadRecu={handleDownloadRecu}
              onDownloadEcheancier={handleDownloadEcheancier}
              onRouvrirSimulateur={handleRouvrirSimulateur}
            />
          ))}
        </div>
      )}
    </div>
  );
}
